import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PLANET_GLYPH } from '../tokens/index.js';
import type { GetChoghadiyaResponse } from '../types/index.js';
import { baseStyles } from '../utils/base-styles.js';
import { MarkupDataController } from '../utils/markup-data.js';
import { capitalize } from '../utils/string.js';

type ChoghadiyaPeriod = GetChoghadiyaResponse['dayChoghadiya'][number];

/**
 * Format an ISO 8601 datetime string to a short local time (HH:MM).
 * Falls back to the raw string when parsing fails.
 */
function fmtTime(iso: string): string {
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return iso;
		return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	} catch {
		return iso;
	}
}

/**
 * Choghadiya muhurta grid. Accepts a GetChoghadiyaResponse and renders
 * 8 daytime and 8 nighttime muhurta tiles in a two-column responsive layout.
 * Good periods are highlighted in green, Bad periods in red.
 */
@customElement('roxy-choghadiya-grid')
export class RoxyChoghadiyaGrid extends LitElement {
	static styles = [
		baseStyles,
		css`.wrap{gap:var(--roxy-space-md,1rem);display:grid}.header{gap:var(--roxy-space-xs,.25rem);display:grid}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);margin:0}.subtitle{font-size:var(--roxy-text-sm,.875rem);color:var(--roxy-muted,#71717a);margin:0}.cho-grid{gap:var(--roxy-space-md,1rem);grid-template-columns:1fr;display:grid}@media (width>=720px){.cho-grid{grid-template-columns:1fr 1fr}}.period-col{gap:var(--roxy-space-xs,.25rem);display:grid}.period-heading{font-size:var(--roxy-text-base,1rem);font-weight:var(--roxy-weight-bold,600);margin:0 0 var(--roxy-space-xs,.25rem);color:var(--roxy-fg,#0a0a0a)}.cho-tile{border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);grid-template-columns:1fr auto;align-items:center;gap:.25em .75em;padding:.55em .85em;display:grid}.cho-tile.good{background:color-mix(in srgb, var(--roxy-success,#22c55e) 18%, transparent);border-color:color-mix(in srgb, var(--roxy-success,#22c55e) 45%, transparent);color:var(--roxy-fg,#0a0a0a)}.cho-tile.bad{background:color-mix(in srgb, var(--roxy-danger,#ef4444) 18%, transparent);border-color:color-mix(in srgb, var(--roxy-danger,#ef4444) 45%, transparent);color:var(--roxy-fg,#0a0a0a)}.cho-tile.neutral{color:var(--roxy-fg,#0a0a0a);background:0 0}.cho-tile.now{outline:2px solid var(--roxy-accent,#f59e0b);outline-offset:1px;box-shadow:0 0 0 4px color-mix(in srgb, var(--roxy-accent,#f59e0b) 18%, transparent)}.now-badge{font-size:var(--roxy-text-xs,.75rem);font-weight:var(--roxy-weight-bold,600);color:var(--roxy-accent-ink,#b45309);text-transform:uppercase;letter-spacing:.06em;margin-left:.4em;display:inline-block}.tile-name{font-size:var(--roxy-text-base,1rem);font-weight:var(--roxy-weight-bold,600);grid-column:1}.tile-time{font-size:var(--roxy-text-xs,.75rem);opacity:.8;white-space:nowrap;text-align:right;grid-area:1/2/3;align-self:center}.tile-lord{font-size:var(--roxy-text-sm,.875rem);opacity:.85;grid-column:1;align-items:center;gap:.25em;display:flex}`,
	];

	constructor() {
		super();
		// Enables hydrating `data` from a direct-child
		// <script type="application/json" class="roxy-data"> for server-rendered
		// and cached consumers. The JavaScript `data` property still wins.
		new MarkupDataController(this);
	}

	@property({ attribute: false })
	data: GetChoghadiyaResponse | null = null;

	/**
	 * True when the current wall-clock time falls inside this period. Both
	 * `start` and `end` are ISO 8601 with timezone, so the comparison is
	 * timezone-aware via the host's `Date` parsing.
	 */
	private isCurrent(period: ChoghadiyaPeriod): boolean {
		const now = Date.now();
		const start = Date.parse(period.start);
		const end = Date.parse(period.end);
		if (Number.isNaN(start) || Number.isNaN(end)) return false;
		return now >= start && now < end;
	}

	private renderTile(period: ChoghadiyaPeriod) {
		const effectClass =
			period.effect === 'Good'
				? 'good'
				: period.effect === 'Bad'
					? 'bad'
					: 'neutral';
		const current = this.isCurrent(period);
		const lordGlyph = PLANET_GLYPH[capitalize(period.lord)] ?? '';
		const timeRange = `${fmtTime(period.start)} - ${fmtTime(period.end)}`;
		return html`<div class="cho-tile ${effectClass}${current ? ' now' : ''}" role="listitem" aria-current="${current ? 'time' : 'false'}"><span class="tile-name">${period.name}${current ? html`<span class="now-badge">Now</span>` : nothing} </span><span class="tile-time" aria-label="Time range">${timeRange}</span> <span class="tile-lord">${lordGlyph ? html`<span aria-hidden="true">${lordGlyph}</span>` : nothing} ${period.lord}</span></div>`;
	}

	render() {
		if (!this.data)
			return html`<div class="roxy-empty" role="status">No choghadiya data</div>`;

		const { date, dayChoghadiya, nightChoghadiya } = this.data;

		return html`<div class="wrap"><div class="header"><h2 class="title">Choghadiya</h2>${date ? html`<p class="subtitle">${date}</p>` : nothing}</div><div class="cho-grid"><section class="period-col" aria-label="Day muhurta periods"><h3 class="period-heading">Day</h3><div role="list" aria-label="Daytime choghadiya">${
							dayChoghadiya && dayChoghadiya.length > 0
								? dayChoghadiya.map((p) => this.renderTile(p))
								: html`<p class="roxy-empty" role="status">No daytime periods</p>`
						}</div></section><section class="period-col" aria-label="Night muhurta periods"><h3 class="period-heading">Night</h3><div role="list" aria-label="Nighttime choghadiya">${
							nightChoghadiya && nightChoghadiya.length > 0
								? nightChoghadiya.map((p) => this.renderTile(p))
								: html`<p class="roxy-empty" role="status">No nighttime periods</p>`
						}</div></section></div></div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-choghadiya-grid': RoxyChoghadiyaGrid;
	}
}
